import * as React from 'react';
import type { ButtonProps } from '../../components/Button';
import { Button } from '../../components/Button';
import type { IconName } from '../../icons/names';
import { cn } from '../../utils';

export interface ToggleButtonOption {
  value: string;
  label?: string;
  icon?: IconName | React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export interface ToggleButtonGroupProps {
  value: string;
  onChange: (value: string) => void;
  options: ToggleButtonOption[];
  size?: ButtonProps['size'];
  className?: string;
  showLabels?: boolean | 'responsive';
}

const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  value,
  onChange,
  options,
  size = 'cm',
  className,
  showLabels = 'responsive'
}) => {
  const showLabel = (label?: string) => {
    if (!label) return false;
    if (showLabels === true) return true;
    if (showLabels === 'responsive') return true;
    return false;
  };

  return (
    <div
      className={cn(
        'inline-flex items-center border border-border-base rounded-md overflow-hidden',
        className
      )}
    >
      {options.map((option, index) => {
        const isSelected = value === option.value;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;
        const showDivider = !isLast;

        return (
          <React.Fragment key={option.value}>
            <Button
              variant={isSelected ? 'primary' : 'ghost'}
              size={size}
              className={cn('gap-xs rounded-none', isFirst && 'rounded-l', isLast && 'rounded-r')}
              onClick={() => onChange(option.value)}
              icon={option.icon}
              iconPosition={option.iconPosition || 'left'}
            >
              {option.label && showLabel(option.label) && (
                <span className={showLabels === 'responsive' ? 'hidden lg:inline' : ''}>
                  {option.label}
                </span>
              )}
            </Button>
            {showDivider && <div className='w-px h-9 bg-border-base' />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

ToggleButtonGroup.displayName = 'ToggleButtonGroup';

export interface ModuleToggleButtonProps extends Omit<ToggleButtonGroupProps, 'className'> {
  title?: React.ReactNode;
  helperText?: React.ReactNode;
  className?: string;
}

export const ToggleButton = React.forwardRef<HTMLDivElement, ModuleToggleButtonProps>(
  (
    {
      title,
      helperText,
      value,
      onChange,
      options,
      className,
      size = 'cm',
      showLabels = 'responsive'
    },
    ref
  ) => (
    <section ref={ref} className={cn('inline-flex flex-col items-start gap-sm', className)}>
      {title && <h3 className='text-sm font-medium text-foreground-base'>{title}</h3>}
      <ToggleButtonGroup
        value={value}
        onChange={onChange}
        options={options}
        size={size}
        showLabels={showLabels}
      />
      {helperText && (
        <p className='text-xs text-foreground-muted lg:text-sm text-balance'>{helperText}</p>
      )}
    </section>
  )
);

ToggleButton.displayName = 'ToggleButton';
